import React, { Fragment, useState } from 'react'
import { Row, Table, Button } from 'react-bootstrap'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  OnChangeFn,
  ColumnFiltersState
} from '@tanstack/react-table'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import EditIcon from '@mui/icons-material/Edit'
import {
  ColumnDef as TanStackColumnDef,
  Table as TanStackTable,
  VisibilityState,
} from "@tanstack/react-table";
import UsersTypeFilterPopover from './UsersTypesFilterPopover'

type FilterType = 'dropdown' | 'text' | 'date' | 'boolean'
export type CustomColumnDef<TData> = TanStackColumnDef<TData> & {
  filterType?: FilterType
  filterOptions?: string[]
  filterFetchUrl?: string
}

interface TableContainerProps<TData = any> {
  columns: any[]
  data:TData[];
  totalRecords: number
  pageIndex: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
  columnFilters: any
  setColumnFilters: any
  colValue: any
  sorting: any
  onSortingChange: any
  manualSorting: boolean
  isBordered: boolean
  isPagination: boolean
  isGlobalFilter: boolean
  SearchPlaceholder: string
  filterOptions?: { [key: string]: any[] }
 onTableReady: (table: TanStackTable<TData>) => void;
}

const UsersTypeTableContainer = ({
  columns,
  data,
  totalRecords,
  pageIndex,
  pageSize,
  onPageChange,
  onPageSizeChange,
  columnFilters,
  setColumnFilters,
  sorting,
  onSortingChange,
  manualSorting = true,
  isBordered = false,
  isPagination = true,
  colValue,
  onTableReady
}: TableContainerProps<any>) => {
 
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const handleColumnFilterChange: OnChangeFn<
    ColumnFiltersState
  > = updaterOrValue => {
    if (!setColumnFilters) return
    setColumnFilters((prev: any) =>
      typeof updaterOrValue === 'function'
        ? updaterOrValue(prev)
        : updaterOrValue
    )
  }

  const table = useReactTable({
    columns,
    data,
    manualPagination: true,
    manualFiltering: true,
    manualSorting,
    pageCount: Math.ceil(totalRecords / pageSize),
    state: {
      sorting,
      pagination: {
        pageIndex,
        pageSize
      },
      columnFilters,
      columnVisibility
    },
    onSortingChange,
    onColumnFiltersChange: handleColumnFilterChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: !manualSorting ? getSortedRowModel() : undefined,
     onColumnVisibilityChange: setColumnVisibility,
  })

  const { getHeaderGroups, getRowModel, getCanPreviousPage, getCanNextPage } =
    table
  const totalPages = Math.ceil(totalRecords / pageSize)
  const maxPageButtons = 5

  const getPageNumbers = () => {
    const pages = []
    if (totalPages <= maxPageButtons) {
      for (let i = 0; i < totalPages; i++) {
        pages.push(i)
      }
    } else {
      if (pageIndex <= 2) {
        pages.push(0, 1, 2, -1, totalPages - 1)
      } else if (pageIndex >= totalPages - 3) {
        pages.push(0, -1, totalPages - 3, totalPages - 2, totalPages - 1)
      } else {
        pages.push(
          0,
          -1,
          pageIndex - 1,
          pageIndex,
          pageIndex + 1,
          -1,
          totalPages - 1
        )
      }
    }
    return pages
  }

  return (
    <Fragment>
      <style>
        {`
    .sticky-column {
      position: sticky;
      right: 0;
      z-index: 10;
      background-color: #fff !important; /* Default background for body cells */
      border-left: 1px solid #dee2e6;
    }

    thead .sticky-column {
      background-color: #EEF0F7 !important; /* Header background specifically */
    }

    .sticky-action {
      box-shadow: -4px 0 6px rgba(0, 0, 0, 0.1);
    }

    .table td,
    .table th {
      background-clip: padding-box;
    }
  `}
      </style>
      <div className='table-responsive table-card'>
        <Table
          className={`table table-centered align-middle table-custom-effect table-nowrap mb-0 ${
            isBordered ? 'table-bordered' : ''
          }`}
          bordered={isBordered}
        >
          <thead className='table-light'>
            {getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => {
                  const isLastColumn = index === headerGroup.headers.length - 1
                  const isSorted = header.column.getIsSorted()
                  const canSort = header.column.getCanSort()

                  const sortIcon =
                    isSorted === 'asc' ? (
                      <ArrowUpwardIcon fontSize='small' />
                    ) : isSorted === 'desc' ? (
                      <ArrowDownwardIcon fontSize='small' />
                    ) : (
                      <UnfoldMoreIcon fontSize='small' />
                    )

                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={`align-middle ${
                        isLastColumn ? 'sticky-column sticky-action' : ''
                      }`}
                    >
                      {!header.isPlaceholder && (
                        <div
                          className='d-flex justify-content-between align-items-center'
                          style={{ gap: 4 }}
                        >
                          {/* Column title + sorting icon */}
                          <div
                            className='d-flex align-items-center'
                            style={{
                              gap: 4,
                              cursor: canSort ? 'pointer' : 'default'
                            }}
                            onClick={
                              canSort
                                ? header.column.getToggleSortingHandler()
                                : undefined
                            }
                          >
                            <span>
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                            </span>
                            {canSort && sortIcon}
                          </div>

                          {/* Filter icon on the right */}
                          {header.column.getCanFilter() ? (
                            <UsersTypeFilterPopover
                              column={header.column}
                              columnDef={header.column.columnDef}
                              columnFilters={columnFilters}
                              setColumnFilters={setColumnFilters}
                              setColValue={colValue}
                                iconColor={
                                  header.column.getFilterValue()
                                    ? "#3258d3"
                                    : "inherit"
                               }
                            />
                          ) : null}
                        </div>
                      )}
                    </th>
                  )
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {getRowModel().rows.length > 0 ? (
              getRowModel().rows.map(row => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell, index) => {
                    const isLastColumn =
                      index === row.getVisibleCells().length - 1
                    return (
                      <td
                        key={cell.id}
                        className={
                          isLastColumn ? 'sticky-column sticky-action' : ''
                        }
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className='text-center text-muted py-3'
                >
                  No Data Found
                  {(columnFilters.length > 0 || sorting.length > 0) &&
                    ''}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {isPagination && (
        <Row className='align-items-center mt-4 pt-3 gy-2'>
          <div className='col-sm'>
            <div className='text-muted'>
              Showing{' '}
              <span className='fw-semibold'>
                {totalRecords === 0 ? 0 : pageIndex * pageSize + 1}
              </span>
              {' - '}
              <span className='fw-semibold'>
                {Math.min((pageIndex + 1) * pageSize, totalRecords)}
              </span>
              {' of '}
              <span className='fw-semibold'>{totalRecords}</span> Results
            </div>
          </div>
          <div className='col-sm-auto'>
            <div className='d-flex flex-wrap align-items-center gap-2'>
              <ul className='pagination pagination-separated mb-0'>
                <li
                  className={`page-item ${
                    !getCanPreviousPage() ? 'disabled' : ''
                  }`}
                >
                  <Button
                    variant='link'
                    className='page-link'
                    disabled={pageIndex === 0}
                    onClick={() => onPageChange(pageIndex - 1)}
                  >
                    &lt;
                  </Button>
                </li>
                {getPageNumbers().map((page, idx) => (
                  <li className='page-item' key={idx}>
                    {page === -1 ? (
                      <span className='page-link'>...</span>
                    ) : (
                      <Button
                        variant='link'
                        className={`page-link ${
                          pageIndex === page ? 'active' : ''
                        }`}
                        onClick={() => onPageChange(page)}
                      >
                        {page + 1}
                      </Button>
                    )}
                  </li>
                ))}
                <li
                  className={`page-item ${!getCanNextPage() ? 'disabled' : ''}`}
                >
                  <Button
                    variant='link'
                    className='page-link'
                    disabled={
                      pageIndex >= Math.ceil(totalRecords / pageSize) - 1
                    }
                    onClick={() => onPageChange(pageIndex + 1)}
                  >
                    &gt;
                  </Button>
                </li>
              </ul>

              <div className='ms-2'>
                <select
                  className='form-select form-select-sm'
                  style={{ minWidth: '100px' }}
                  value={pageSize}
                  onChange={e => onPageSizeChange(Number(e.target.value))}
                >
                  {[10, 20, 30, 50].map(size => (
                    <option key={size} value={size}>
                      Show {size}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </Row>
      )}
    </Fragment>
  )
}

export default UsersTypeTableContainer
